import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, ListGroup, ListGroupItem, Spinner } from "reactstrap";

import { TenantEnrichment } from "../../services/adminCsService";

type EnrichmentCardProps = {
  enrichment: TenantEnrichment | null;
  loading?: boolean;
  tenantName?: string;
};

const resolveScoreColor = (score: number): string => {
  if (score >= 75) {
    return "success";
  }

  if (score >= 45) {
    return "warning";
  }

  return "secondary";
};

const EnrichmentCard = ({ enrichment, loading = false, tenantName }: EnrichmentCardProps) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <p className="text-muted mt-2 mb-0">{t("admin.cs.enrichment.loading")}</p>
      </div>
    );
  }

  if (!enrichment) {
    return (
      <p className="text-muted mb-0">{t("admin.cs.enrichment.notAvailable")}</p>
    );
  }

  const signalEntries = Object.entries(enrichment.signals ?? {});

  return (
    <div>
      {tenantName ? (
        <h5 className="mb-3">{tenantName}</h5>
      ) : null}

      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="text-muted">{t("admin.cs.enrichment.score")}</span>
        <Badge color={resolveScoreColor(enrichment.lead_score)} className="fs-12">
          {enrichment.lead_score}
        </Badge>
        <Badge color="info" className="fs-12">
          {t(`admin.cs.enrichment.priority.${enrichment.summary.priority}`, {
            defaultValue: enrichment.summary.priority,
          })}
        </Badge>
      </div>

      <p className="mb-3">{enrichment.summary.headline}</p>

      <p className="text-muted small mb-3">
        {t("admin.cs.enrichment.estimatedSize", {
          size: t(`admin.cs.enrichment.size.${enrichment.summary.estimated_size}`, {
            defaultValue: enrichment.summary.estimated_size,
          }),
        })}
      </p>

      {enrichment.summary.highlights?.length > 0 ? (
        <div className="mb-3 d-flex flex-wrap gap-1">
          {enrichment.summary.highlights.map((highlight) => (
            <Badge key={highlight} color="primary" className="bg-primary-subtle text-primary">
              {t(`admin.cs.enrichment.highlights.${highlight}`, {
                defaultValue: highlight,
              })}
            </Badge>
          ))}
        </div>
      ) : null}

      <h6 className="mb-2">{t("admin.cs.enrichment.signalsTitle")}</h6>
      <ListGroup className="mb-3">
        {signalEntries.map(([key, value]) => (
          <ListGroupItem
            key={key}
            className="d-flex justify-content-between align-items-center"
          >
            <span>{t(`admin.cs.enrichment.signals.${key}`, { defaultValue: key })}</span>
            <Badge color={value ? "success" : "light"} className={value ? "" : "text-muted"}>
              {value ? t("admin.cs.enrichment.signalYes") : t("admin.cs.enrichment.signalNo")}
            </Badge>
          </ListGroupItem>
        ))}
      </ListGroup>

      <h6 className="mb-2">{t("admin.cs.enrichment.sourcesTitle")}</h6>
      <ListGroup>
        {enrichment.sources.map((source, index) => (
          <ListGroupItem key={`${source.type}-${index}`}>
            <div className="fw-medium">
              {t(`admin.cs.enrichment.sourceTypes.${source.type}`, {
                defaultValue: source.type,
              })}
            </div>
            <a href={source.url} target="_blank" rel="noreferrer" className="small">
              {source.url}
            </a>
          </ListGroupItem>
        ))}
      </ListGroup>
    </div>
  );
};

export default EnrichmentCard;
